import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WHATSAPP } from '../data/contacto';

const CHECKS = [
  { cat: 'Seguridad', items: [
    { label: 'Información sensible expuesta',           status: 'warn',  detail: 'Hay datos que deberían estar escondidos y están a la vista' },
    { label: 'La puerta de acceso no tiene cerrojo',    status: 'error', detail: 'Alguien podría intentar entrar muchas veces sin que nada lo detenga' },
    { label: 'Las conexiones externas están protegidas', status: 'ok',   detail: 'Solo se comunica con quienes debe' },
    { label: 'Datos personales sin protección',          status: 'warn',  detail: 'Algunos datos de usuarios están guardados sin el cuidado necesario' },
  ]},
  { cat: 'Cómo responde cuando crece', items: [
    { label: 'Los errores no se manejan bien',         status: 'error', detail: 'Si algo falla, el usuario ve cosas que no debería ver' },
    { label: 'Consultas lentas cuando hay datos',      status: 'warn',  detail: 'Cada elemento que se muestra genera trabajo innecesario' },
    { label: 'Las búsquedas van a ser lentas',         status: 'error', detail: 'Con más usuarios, la app va a tardar más de lo razonable' },
    { label: 'El código está bien organizado',         status: 'ok',   detail: 'La estructura interna está clara y es fácil de mantener' },
  ]},
  { cat: 'Experiencia del usuario', items: [
    { label: 'El usuario no sabe qué está pasando',    status: 'warn',  detail: 'Cuando hace algo, no sabe si está cargando o si se colgó' },
    { label: 'Los errores se avisan tarde',            status: 'error', detail: 'Si algo está mal en un formulario, se entera recién al final' },
    { label: 'Funciona bien en el celular',            status: 'ok',   detail: 'Se ve y usa bien desde cualquier dispositivo' },
    { label: 'Las acciones no se confirman',           status: 'warn',  detail: 'Al hacer algo, no queda claro si funcionó o no' },
  ]},
];

const STATUS_CONFIG = {
  ok:    { label: 'Bien',      color: '#29D880', bg: '#29D88015', icon: '✓' },
  warn:  { label: 'A revisar', color: '#F59E0B', bg: '#F59E0B15', icon: '⚠' },
  error: { label: 'Urgente',   color: '#FF6B6B', bg: '#FF6B6B15', icon: '✕' },
};

const PRIORITY = [
  { priority: '1', title: 'Poner cerrojo en la puerta de acceso',       effort: '2–4hs', impact: 'Urgente — alguien podría entrar sin permiso',   color: '#FF6B6B' },
  { priority: '2', title: 'Hacer que las búsquedas sean rápidas',       effort: '1–2hs', impact: 'Alto — va a crecer y tiene que aguantar',        color: '#FF6B6B' },
  { priority: '3', title: 'Esconder la información sensible',            effort: '1hs',   impact: 'Alto — seguridad básica que falta',              color: '#F59E0B' },
  { priority: '4', title: 'Que los errores se muestren bien',            effort: '3–5hs', impact: 'Medio — mejor experiencia y más seguridad',      color: '#F59E0B' },
  { priority: '5', title: 'Que el usuario siempre sepa qué está pasando', effort: '4–6hs', impact: 'Medio — se percibe más confiable y profesional', color: '#29D880' },
];

const useReveal = (delay = 0) => {
  const [v, setV] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setV(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return v;
};

const CheckRow = ({ item, delay }) => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  const cfg = STATUS_CONFIG[item.status];
  return (
    <div
      className="flex items-start gap-3 py-2.5 border-b border-white/[0.05] last:border-0 transition-all duration-500"
      style={{ opacity: shown ? 1 : 0, transform: shown ? 'translateX(0)' : 'translateX(-10px)' }}
    >
      <div
        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold mt-0.5"
        style={{ background: cfg.bg, color: cfg.color }}
      >
        {cfg.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-gray-700 text-sm">{item.label}</p>
        <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">{item.detail}</p>
      </div>
      <span
        className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5"
        style={{ background: cfg.bg, color: cfg.color }}
      >
        {cfg.label}
      </span>
    </div>
  );
};

const DiagnosticReport = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const problem     = state?.problem     || 'No especificado';
  const lastReview  = state?.lastReview  || 'No';
  const existingUrl = state?.existingUrl || null;

  const header   = useReveal(0);
  const summary  = useReveal(300);
  const checks   = useReveal(600);
  const priority = useReveal(1800);
  const cta      = useReveal(2400);

  const errCount  = CHECKS.flatMap(c => c.items).filter(i => i.status === 'error').length;
  const warnCount = CHECKS.flatMap(c => c.items).filter(i => i.status === 'warn').length;
  const okCount   = CHECKS.flatMap(c => c.items).filter(i => i.status === 'ok').length;
  const score     = Math.round((okCount / (errCount + warnCount + okCount)) * 10);

  return (
    <div className="guia-bg min-h-screen font-sans text-gray-900">

      <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06]">
        <span className="text-gray-800 font-semibold tracking-[0.2em] text-sm">GuIA</span>
        <button onClick={() => navigate('/')} className="text-gray-300 text-xs hover:text-gray-600 transition-colors">
          ← nueva consulta
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">

        {/* Header */}
        <div
          className="mb-8 transition-all duration-700"
          style={{ opacity: header ? 1 : 0, transform: header ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <p className="text-gray-400 text-xs tracking-[0.4em] uppercase mb-3">
            {existingUrl ? `Revisión de ${existingUrl}` : 'Lo que encontramos.'}
          </p>
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <div className="flex gap-2 flex-wrap mb-2">
                <span className="text-xs px-3 py-1.5 rounded-full bg-black/[0.04] border border-black/[0.08] text-gray-500">
                  {problem}
                </span>
                {existingUrl && (
                  <a
                    href={existingUrl.startsWith('http') ? existingUrl : `https://${existingUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs px-3 py-1.5 rounded-full border border-guia-mint/20 text-guia-mint/70 hover:text-guia-mint hover:border-guia-mint/50 transition-colors"
                  >
                    {existingUrl}
                  </a>
                )}
                <span className="text-xs px-3 py-1.5 rounded-full border border-black/[0.06] text-gray-300">
                  Revisión externa: <span className="text-gray-400">{lastReview}</span>
                </span>
              </div>
            </div>
            <div className="text-center flex-shrink-0">
              <div
                className="w-16 h-16 rounded-full border-2 flex items-center justify-center"
                style={{ borderColor: score >= 7 ? '#29D880' : score >= 4 ? '#F59E0B' : '#FF6B6B' }}
              >
                <span
                  className="text-2xl font-bold"
                  style={{ color: score >= 7 ? '#29D880' : score >= 4 ? '#F59E0B' : '#FF6B6B' }}
                >
                  {score}
                </span>
              </div>
              <p className="text-gray-300 text-[10px] mt-1">/ 10</p>
            </div>
          </div>
        </div>

        {/* Summary counters */}
        <div
          className="grid grid-cols-3 gap-3 mb-8 transition-all duration-700"
          style={{ opacity: summary ? 1 : 0, transform: summary ? 'translateY(0)' : 'translateY(16px)' }}
        >
          {[
            { count: errCount,  label: 'Urgente',    color: '#FF6B6B' },
            { count: warnCount, label: 'A revisar',  color: '#F59E0B' },
            { count: okCount,   label: 'Está bien',  color: '#29D880' },
          ].map((s, i) => (
            <div key={i} className="rounded-xl border border-black/[0.08] bg-black/[0.02] px-4 py-4 text-center">
              <p className="text-3xl font-light mb-1" style={{ color: s.color }}>{s.count}</p>
              <p className="text-gray-400 text-xs">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Checks */}
        <div
          className="mb-8 transition-all duration-700 space-y-4"
          style={{ opacity: checks ? 1 : 0, transform: checks ? 'translateY(0)' : 'translateY(16px)' }}
        >
          {CHECKS.map((cat, ci) => (
            <div key={ci} className="rounded-2xl border border-black/[0.07] bg-black/[0.02] overflow-hidden">
              <div className="px-5 py-3 border-b border-black/[0.06]">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider">{cat.cat}</p>
              </div>
              <div className="px-5">
                {cat.items.map((item, ii) => (
                  <CheckRow key={ii} item={item} delay={checks ? 700 + (ci * 4 + ii) * 120 : 99999} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Priority */}
        <div
          className="mb-8 transition-all duration-700"
          style={{ opacity: priority ? 1 : 0, transform: priority ? 'translateY(0)' : 'translateY(16px)' }}
        >
          <p className="text-gray-400 text-xs tracking-[0.4em] uppercase mb-4">Qué resolver primero</p>
          <div className="space-y-2">
            {PRIORITY.map((p, i) => (
              <div key={i} className="flex items-center gap-4 rounded-xl border border-black/[0.06] bg-black/[0.02] px-4 py-3">
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ background: `${p.color}20`, color: p.color }}
                >
                  {p.priority}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 text-sm">{p.title}</p>
                  <p className="text-gray-400 text-xs">{p.impact}</p>
                </div>
                <span className="text-gray-300 text-xs flex-shrink-0">{p.effort}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div
          className="transition-all duration-700"
          style={{ opacity: cta ? 1 : 0, transform: cta ? 'translateY(0)' : 'translateY(16px)' }}
        >
          <div className="rounded-2xl border border-guia-mint/20 bg-guia-mint/[0.04] px-6 py-7 text-center">
            <p className="text-gray-700 text-base mb-1 font-light">
              {errCount} {errCount === 1 ? 'cosa urgente' : 'cosas urgentes'} antes de seguir.
            </p>
            <p className="text-gray-400 text-sm mb-1 leading-relaxed">
              GuIA se encarga. Con vos, al ritmo que necesitás.
            </p>
            <p className="text-gray-300 text-xs mb-6">La revisión es sin costo. Lo que decidís hacer después, a acordar.</p>
            <div className="flex justify-center gap-3 flex-wrap">
              <a
                href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Hola GuIA, quiero resolver el diagnóstico de mi app')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-7 py-3.5 rounded-full bg-guia-mint text-black text-sm font-semibold hover:bg-guia-mint/90 transition-all hover:scale-[1.02]"
                style={{ boxShadow: '0 0 32px rgba(41,216,128,0.2)' }}
              >
                Hablar por WhatsApp →
              </a>
              <button
                onClick={() => navigate('/')}
                className="px-7 py-3.5 rounded-full border border-black/[0.08] text-gray-500 text-sm hover:border-black/20 hover:text-gray-600 transition-all"
              >
                Nueva consulta
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DiagnosticReport;
